import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getCreditInfoByStore } from '../../api/credit.api';
import {
  listLicenseMembers,
  addLicenseMember,
  deleteLicenseMember,
  resetMemberPassword,
  type LicenseMemberView,
} from '../../api/license.api';
import styles from './LicenseMembersSection.module.css';

const MAX_MEMBERS = 2;

/**
 * 매장 추가 관리자 (옵션 A: OWNER + MEMBER 최대 2명)
 *
 * 권한 안내 표시 + 추가/삭제/비번리셋 기능 제공.
 * OWNER 만 표시되며, MEMBER 가 접근 시 안내 문구만 노출.
 */
export default function LicenseMembersSection() {
  const { isOwner, role, licenseKey, storeId, isDemo } = useAuth();
  const [licenseId, setLicenseId] = useState<string | null>(null);
  const [members, setMembers] = useState<LicenseMemberView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 추가 폼
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // 비번 리셋 폼 (해당 행에서만)
  const [resetTargetId, setResetTargetId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const load = useCallback(async () => {
    if (!storeId || !licenseKey || !isOwner) return;
    setIsLoading(true);
    setError(null);
    try {
      const info = await getCreditInfoByStore(storeId);
      setLicenseId(info.licenseId);
      const list = await listLicenseMembers(info.licenseId, licenseKey);
      setMembers(list);
    } catch (err: any) {
      setError(err?.message ?? '관리자 목록을 불러올 수 없습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [storeId, licenseKey, isOwner]);

  useEffect(() => {
    load();
  }, [load]);

  // MEMBER 가 접근한 경우: 안내 문구만 노출
  if (role === 'MEMBER') {
    return (
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>매장 관리자</h2>
        <div className={styles.memberOnlyNotice}>
          매장 관리자(추가 계정) 관리는 <strong>매장 사장님(OWNER)</strong>만 가능합니다.
          관리자 추가/삭제가 필요하면 사장님께 요청해주세요.
        </div>
      </div>
    );
  }

  if (!isOwner) return null;

  const handleAdd = async () => {
    if (!licenseId || !licenseKey || isAdding) return;
    if (isDemo) {
      setError('데모 모드에서는 관리자 추가가 저장되지 않습니다.');
      return;
    }
    setError(null);
    if (!newName.trim()) {
      setError('관리자 이름을 입력해주세요.');
      return;
    }
    if (newPassword.length < 8) {
      setError('비밀번호는 8자 이상이어야 합니다.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    setIsAdding(true);
    try {
      await addLicenseMember(licenseId, licenseKey, newName.trim(), newPassword);
      setNewName('');
      setNewPassword('');
      setNewPasswordConfirm('');
      setShowAddForm(false);
      await load();
    } catch (err: any) {
      setError(err?.message ?? '관리자 추가 실패');
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (member: LicenseMemberView) => {
    if (!licenseId || !licenseKey) return;
    if (isDemo) {
      setError('데모 모드에서는 관리자 삭제가 저장되지 않습니다.');
      return;
    }
    if (!window.confirm(`관리자 "${member.memberName}" (${member.loginId})를 삭제하시겠습니까?\n해당 계정은 즉시 로그인할 수 없게 됩니다.`)) {
      return;
    }
    try {
      await deleteLicenseMember(licenseId, member.id, licenseKey);
      await load();
    } catch (err: any) {
      setError(err?.message ?? '삭제 실패');
    }
  };

  const startReset = (memberId: string) => {
    setResetTargetId(memberId);
    setResetPassword('');
    setResetPasswordConfirm('');
  };

  const cancelReset = () => {
    setResetTargetId(null);
    setResetPassword('');
    setResetPasswordConfirm('');
  };

  const handleReset = async () => {
    if (!licenseId || !licenseKey || !resetTargetId || isResetting) return;
    if (isDemo) {
      setError('데모 모드에서는 비밀번호 리셋이 저장되지 않습니다.');
      return;
    }
    if (resetPassword.length < 8) {
      setError('새 비밀번호는 8자 이상이어야 합니다.');
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    setIsResetting(true);
    try {
      await resetMemberPassword(licenseId, resetTargetId, licenseKey, resetPassword);
      cancelReset();
      await load();
      window.alert('비밀번호가 변경되었습니다. 해당 관리자에게 새 비밀번호를 안내해주세요.');
    } catch (err: any) {
      setError(err?.message ?? '비밀번호 리셋 실패');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>매장 관리자</h2>

      {/* 권한 안내 */}
      <div className={styles.permissionInfo}>
        <div className={styles.permissionTitle}>권한 안내</div>
        <div className={styles.permissionGrid}>
          <div className={styles.permissionRow}>
            <span className={styles.roleTagOwner}>사장님 (현재 본인)</span>
            <span className={styles.permissionDesc}>모든 권한</span>
          </div>
          <div className={styles.permissionRow}>
            <span className={styles.roleTagMember}>추가 관리자</span>
            <span className={styles.permissionDesc}>
              <strong>가능</strong>: 대기관리(호출/입장/노쇼), 메뉴 설정, 사용 내역 조회, 문의/건의, 본인 비밀번호 변경
              <br />
              <strong>불가</strong>: 매장 설정, 크레딧 충전·결제, 관리자 추가/삭제, 라이선스 관련 작업
            </span>
          </div>
        </div>
        <div className={styles.permissionNote}>
          매장 1개당 추가 관리자는 최대 <strong>{MAX_MEMBERS}명</strong>까지 등록할 수 있으며,
          로그인 ID는 사장님 ID 뒤에 자동으로 1, 2 가 붙습니다.
        </div>
      </div>

      {error && <div className={styles.errorBox}>{error}</div>}

      {/* 멤버 목록 */}
      {isLoading ? (
        <div className={styles.loading}>로딩 중...</div>
      ) : members.length === 0 ? (
        <div className={styles.empty}>등록된 추가 관리자가 없습니다.</div>
      ) : (
        <ul className={styles.memberList}>
          {members.map((m) => (
            <li key={m.id} className={styles.memberItem}>
              <div className={styles.memberInfo}>
                <div className={styles.memberHeader}>
                  <span className={styles.memberName}>{m.memberName}</span>
                  <span className={styles.memberSlot}>#{m.slot}</span>
                </div>
                <div className={styles.memberMeta}>
                  로그인 ID: <code className={styles.code}>{m.loginId}</code>
                  {m.lastLoginAt && (
                    <>
                      <span className={styles.metaDot}>·</span>
                      마지막 로그인 {new Date(m.lastLoginAt).toLocaleString('ko-KR')}
                    </>
                  )}
                </div>
              </div>

              {resetTargetId === m.id ? (
                <div className={styles.resetForm}>
                  <input
                    type="password"
                    className={styles.input}
                    placeholder="새 비밀번호 (8자 이상)"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={isResetting}
                  />
                  <input
                    type="password"
                    className={styles.input}
                    placeholder="새 비밀번호 확인"
                    value={resetPasswordConfirm}
                    onChange={(e) => setResetPasswordConfirm(e.target.value)}
                    autoComplete="new-password"
                    disabled={isResetting}
                  />
                  <div className={styles.btnRow}>
                    <button type="button" className={styles.btnPrimary} onClick={handleReset} disabled={isResetting}>
                      {isResetting ? '...' : '저장'}
                    </button>
                    <button type="button" className={styles.btnGhost} onClick={cancelReset} disabled={isResetting}>
                      취소
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.actions}>
                  <button type="button" className={styles.btnGhost} onClick={() => startReset(m.id)}>
                    비밀번호 변경
                  </button>
                  <button type="button" className={styles.btnDanger} onClick={() => handleDelete(m)}>
                    삭제
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* 추가 폼 / 추가 버튼 */}
      {members.length < MAX_MEMBERS && (
        showAddForm ? (
          <div className={styles.addForm}>
            <div className={styles.addRow}>
              <input
                type="text"
                className={styles.input}
                placeholder="관리자 이름 (예: 김알바)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                disabled={isAdding}
              />
            </div>
            <div className={styles.addRow}>
              <input
                type="password"
                className={styles.input}
                placeholder="비밀번호 (8자 이상)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                disabled={isAdding}
              />
            </div>
            <div className={styles.addRow}>
              <input
                type="password"
                className={styles.input}
                placeholder="비밀번호 확인"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                autoComplete="new-password"
                disabled={isAdding}
              />
            </div>
            <div className={styles.btnRow}>
              <button type="button" className={styles.btnPrimary} onClick={handleAdd} disabled={isAdding}>
                {isAdding ? '추가 중...' : '관리자 추가'}
              </button>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={() => {
                  setShowAddForm(false);
                  setNewName('');
                  setNewPassword('');
                  setNewPasswordConfirm('');
                  setError(null);
                }}
                disabled={isAdding}
              >
                취소
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className={styles.addButton} onClick={() => setShowAddForm(true)}>
            + 관리자 추가 ({members.length} / {MAX_MEMBERS})
          </button>
        )
      )}
    </div>
  );
}
